/**
 * DataSourceIndicator - Displays data source/lineage information
 * Shows whether data is real, derived, synthetic, or fallback
 */

import clsx from 'clsx'

type DataSourceType = 'real' | 'registry' | 'derived' | 'calculated' | 'synthetic' | 'fallback' | 'timestamp_only'

interface DataSourceIndicatorProps {
  source: DataSourceType | string
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  className?: string
}

const sourceConfig: Record<string, { bg: string; text: string; icon: string; label: string; description: string }> = {
  real: {
    bg: 'bg-status-healthy/15',
    text: 'text-status-healthy',
    icon: '●',
    label: 'Live',
    description: 'Real-time data from sensors/models'
  },
  registry: {
    bg: 'bg-status-healthy/15',
    text: 'text-status-healthy',
    icon: '●',
    label: 'Real',
    description: 'Data from trained model registry'
  },
  derived: {
    bg: 'bg-status-info/15',
    text: 'text-status-info',
    icon: '◐',
    label: 'Derived',
    description: 'Calculated from real data'
  },
  calculated: {
    bg: 'bg-status-info/15',
    text: 'text-status-info',
    icon: '◐',
    label: 'Calculated',
    description: 'Computed from base metrics'
  },
  synthetic: {
    bg: 'bg-status-warning/15',
    text: 'text-status-warning',
    icon: '○',
    label: 'Simulated',
    description: 'Generated/simulated data'
  },
  fallback: {
    bg: 'bg-surface-600/15',
    text: 'text-surface-400',
    icon: '◌',
    label: 'Fallback',
    description: 'Default values (API unavailable)'
  },
  timestamp_only: {
    bg: 'bg-surface-600/15',
    text: 'text-surface-400',
    icon: '◷',
    label: 'Timestamp',
    description: 'Current time, not actual event time'
  }
}

const sizes = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm'
}

export default function DataSourceIndicator({
  source,
  size = 'sm',
  showLabel = true,
  className
}: DataSourceIndicatorProps) {
  const config = sourceConfig[source] || sourceConfig.fallback

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1',
        config.text,
        sizes[size],
        className
      )}
      title={config.description}
    >
      <span>{config.icon}</span>
      {showLabel && <span>{config.label}</span>}
    </span>
  )
}

// Tooltip version for more detailed display
export function DataSourceTooltip({
  sources,
  className
}: {
  sources: Record<string, string>
  className?: string
}) {
  const entries = Object.entries(sources).filter(([, value]) => value)

  if (entries.length === 0) return null

  return (
    <div className={clsx('text-xs space-y-1', className)}>
      <div className="text-surface-400 font-medium mb-1">Data Sources:</div>
      {entries.map(([key, value]) => {
        const config = sourceConfig[value] || sourceConfig.fallback
        return (
          <div key={key} className="flex items-center gap-2">
            <span className={config.text}>{config.icon}</span>
            <span className="text-surface-300 capitalize">{key.replace(/_/g, ' ')}:</span>
            <span className={config.text}>{config.label}</span>
          </div>
        )
      })}
    </div>
  )
}

// Summary badge showing overall data quality
export function DataQualityBadge({
  sources,
  size = 'sm',
  className
}: {
  sources?: Record<string, string>
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  if (!sources) return null

  const values = Object.values(sources).filter(Boolean)
  const realCount = values.filter(v => v === 'real' || v === 'registry').length
  const syntheticCount = values.filter(v => v === 'synthetic' || v === 'fallback').length

  let config: { bg: string; text: string; label: string }

  if (syntheticCount === 0 && realCount > 0) {
    config = { bg: 'bg-status-healthy/15', text: 'text-status-healthy', label: 'Real Data' }
  } else if (realCount >= syntheticCount) {
    config = { bg: 'bg-status-info/15', text: 'text-status-info', label: 'Mixed Data' }
  } else {
    config = { bg: 'bg-status-warning/15', text: 'text-status-warning', label: 'Simulated' }
  }

  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
    lg: 'px-2.5 py-1 text-sm'
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded font-medium',
        config.bg,
        config.text,
        sizeClasses[size],
        className
      )}
      title={`${realCount} real, ${values.length - realCount} derived/synthetic fields`}
    >
      {config.label}
    </span>
  )
}
